import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { probationApi } from '../../api/probation';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../utils/formatDate';

const STATUS_LABELS: Record<string, string> = {
  in_progress: 'In Progress', under_review: 'Awaiting HR Decision', confirmed: 'Confirmed', extended: 'Extended', non_confirmed: 'Not Confirmed',
};

/** Own probation status (read-only - decisions are HR's alone), plus a real "my reports awaiting my recommendation" section for supervisors. */
export function MyProbationPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const mineQuery = useQuery({ queryKey: ['my-probation'], queryFn: () => probationApi.mine() });
  const pendingQuery = useQuery({ queryKey: ['probation-pending-my-recommendation'], queryFn: () => probationApi.pendingForMyRecommendation() });

  const [recommendation, setRecommendation] = useState<Record<number, { recommendation: string; comments: string }>>({});
  const recommendMutation = useMutation({
    mutationFn: ({ id, v }: { id: number; v: { recommendation: string; comments: string } }) =>
      probationApi.submitSupervisorRecommendation(id, { recommendation: v.recommendation as 'confirm' | 'extend' | 'non_confirm', comments: v.comments }),
    onSuccess: () => { showSuccess('Recommendation submitted to HR.'); queryClient.invalidateQueries({ queryKey: ['probation-pending-my-recommendation'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit recommendation.'),
  });

  return (
    <div className="page">
      <Link to="/staff">&larr; Back to Dashboard</Link>
      <PageHeader title="My Probation" />

      <div className="panel">
        <SectionHeader title="My Probation Status" />
        <div className="panel-body">
        {mineQuery.data && (
          <DataTable
            rows={mineQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage="No probation record on file."
            columns={[
              { header: 'Start', render: (r) => r.start_date },
              { header: 'Expected End', render: (r) => r.expected_end_date },
              { header: 'Status', render: (r) => <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
              { header: 'HR Decision', render: (r) => r.hr_decision ? r.hr_decision.replace('_', ' ') : '-' },
            ]}
          />
        )}
      </div>
      </div>

      {pendingQuery.data && pendingQuery.data.data.length > 0 && (
        <div className="panel">
          <SectionHeader title="Direct Reports Awaiting Your Recommendation" />
          <div className="panel-body">
          {pendingQuery.data.data.map((r) => (
            <div key={r.id} className="inline-form" style={{ marginBottom: 12 }}>
              <strong>{r.employee_name}</strong> (ends {formatDate(r.expected_end_date)})
              <select value={recommendation[r.id]?.recommendation ?? ''} onChange={(e) => setRecommendation((s) => ({ ...s, [r.id]: { recommendation: e.target.value, comments: s[r.id]?.comments ?? '' } }))}>
                <option value="">- recommendation -</option>
                <option value="confirm">Recommend Confirmation</option>
                <option value="extend">Recommend Extension</option>
                <option value="non_confirm">Recommend Non-Confirmation</option>
              </select>
              <input placeholder="Comments (required)" value={recommendation[r.id]?.comments ?? ''} onChange={(e) => setRecommendation((s) => ({ ...s, [r.id]: { recommendation: s[r.id]?.recommendation ?? '', comments: e.target.value } }))} />
              <button
                onClick={() => recommendation[r.id]?.recommendation && recommendation[r.id]?.comments ? recommendMutation.mutate({ id: r.id, v: recommendation[r.id] }) : showError('Select a recommendation and add comments.')}
                disabled={recommendMutation.isPending}
              >
                Submit
              </button>
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}
